/**
 * useKanbanColumns Hook
 *
 * Hook per la gestione delle colonne Kanban dinamiche.
 * Supporta operazioni CRUD per colonne e gestione alert scadenze.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useCacheConfig } from '../contexts/cache-config-context';
import { kanbanColumnsService } from '../services/api/kanban-columns';
import type {
  CreateKanbanColumnRequest,
  UpdateKanbanColumnRequest,
  ReorderKanbanColumnsRequest,
  MoveTaskToDynamicColumnRequest,
  SnoozeAlertRequest,
  DismissAlertRequest,
  UserAlertPreferences,
  KanbanBoardFilters,
  DynamicKanbanBoard,
  DynamicKanbanColumnData,
  ProjectTaskSummary,
} from '../types/api';

/**
 * Normalizza i filtri board per una chiave di query stabile: ordina `assigneeIds`
 * e rimuove i valori undefined/vuoti, così filtri equivalenti (anche se costruiti con
 * ordine diverso o proprietà undefined esplicite) producono la stessa query key.
 */
function normalizeFilters(filters: KanbanBoardFilters = {}): KanbanBoardFilters {
  const normalized: KanbanBoardFilters = {};
  if (filters.requestId !== undefined) normalized.requestId = filters.requestId;
  if (filters.assigneeIds && filters.assigneeIds.length > 0) {
    normalized.assigneeIds = [...filters.assigneeIds].sort((a, b) => a - b);
  }
  if (filters.dateField) normalized.dateField = filters.dateField;
  if (filters.from) normalized.from = filters.from;
  if (filters.to) normalized.to = filters.to;
  return normalized;
}

// Query keys
export const kanbanColumnKeys = {
  all: ['kanbanColumns'] as const,
  columns: () => [...kanbanColumnKeys.all, 'list'] as const,
  column: (id: number) => [...kanbanColumnKeys.all, 'detail', id] as const,
  boards: () => [...kanbanColumnKeys.all, 'board'] as const,
  board: (filters: KanbanBoardFilters = {}) => [...kanbanColumnKeys.boards(), normalizeFilters(filters)] as const,
  alerts: () => [...kanbanColumnKeys.all, 'alerts'] as const,
  alertPreferences: () => [...kanbanColumnKeys.all, 'alertPreferences'] as const,
  lookups: (search: string, withTasksOnly: boolean, includeId?: number) =>
    [...kanbanColumnKeys.all, 'lookups', 'requests', search, withTasksOnly, includeId] as const,
};

/**
 * Aggiornamento puro (nessuna mutazione in place) dello snapshot board per lo spostamento
 * ottimistico di un task: lo rimuove dalla colonna sorgente e lo inserisce nella colonna
 * di destinazione, prima di `beforeTaskId` (o in fondo se null/undefined/non trovato).
 * Verrà sostituito da `moveTaskInBoard` in `board-utils.ts` quando i componenti Kanban
 * (prossima ondata) lo introdurranno; per ora resta locale a questo hook.
 */
function moveTaskInBoard(
  board: DynamicKanbanBoard,
  taskId: number,
  columnId: number,
  beforeTaskId?: number | null
): DynamicKanbanBoard {
  let movedTask: ProjectTaskSummary | undefined;

  const withoutTask: DynamicKanbanColumnData[] = board.columns.map((col) => {
    const idx = col.tasks.findIndex((t) => t.id === taskId);
    if (idx === -1) return col;
    movedTask = col.tasks[idx];
    const tasks = col.tasks.slice();
    tasks.splice(idx, 1);
    return { ...col, tasks };
  });

  if (!movedTask) return board;
  const updatedTask: ProjectTaskSummary = { ...movedTask, kanbanColumnId: columnId };

  const withTask: DynamicKanbanColumnData[] = withoutTask.map((col) => {
    if (col.id !== columnId) return col;
    const tasks = col.tasks.slice();
    const insertIdx = beforeTaskId != null ? tasks.findIndex((t) => t.id === beforeTaskId) : -1;
    if (insertIdx === -1) {
      tasks.push(updatedTask);
    } else {
      tasks.splice(insertIdx, 0, updatedTask);
    }
    return { ...col, tasks };
  });

  return { ...board, columns: withTask };
}

// ============== Columns Queries ==============

/**
 * Hook per ottenere tutte le colonne Kanban
 */
export const useKanbanColumns = () => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: kanbanColumnKeys.columns(),
    queryFn: kanbanColumnsService.getAllColumns,
    staleTime: getCacheTime('tasks'),
  });
};

/**
 * Hook per ottenere una colonna specifica
 */
export const useKanbanColumn = (id: number) => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: kanbanColumnKeys.column(id),
    queryFn: () => kanbanColumnsService.getColumnById(id),
    staleTime: getCacheTime('tasks'),
    enabled: id > 0,
  });
};

/**
 * Hook per ottenere la board dinamica, con filtri persona/richiesta/periodo.
 * `keepPreviousData` evita che la board si svuoti visivamente mentre i filtri cambiano.
 */
export const useDynamicKanbanBoard = (filters: KanbanBoardFilters = {}) => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: kanbanColumnKeys.board(filters),
    queryFn: () => kanbanColumnsService.getDynamicBoard(filters),
    placeholderData: keepPreviousData,
    staleTime: getCacheTime('tasks'),
  });
};

/**
 * Hook per la ricerca richieste (RequestLookupSelect): ricerca lato server con cache lunga,
 * dato che l'elenco richieste cambia meno frequentemente dei task. Una ricerca vuota
 * restituisce comunque le richieste più recenti/rilevanti lato backend.
 */
export const useRequestLookups = (search: string, withTasksOnly: boolean, includeId?: number) => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: kanbanColumnKeys.lookups(search, withTasksOnly, includeId),
    queryFn: () => kanbanColumnsService.getRequestLookups({ search, withTasksOnly, includeId }),
    staleTime: getCacheTime('externalClients'),
  });
};

// ============== Columns Mutations ==============

/**
 * Hook per creare una nuova colonna
 */
export const useCreateKanbanColumn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateKanbanColumnRequest) => kanbanColumnsService.createColumn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.all });
      notifications.show({
        title: 'Colonna creata',
        message: 'La colonna e stata creata con successo',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile creare la colonna',
        color: 'red',
      });
    },
  });
};

/**
 * Hook per aggiornare una colonna
 */
export const useUpdateKanbanColumn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateKanbanColumnRequest }) =>
      kanbanColumnsService.updateColumn(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.all });
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.column(id) });
      notifications.show({
        title: 'Colonna aggiornata',
        message: 'La colonna e stata aggiornata con successo',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile aggiornare la colonna',
        color: 'red',
      });
    },
  });
};

/**
 * Hook per eliminare una colonna. Se contiene task, `moveTasksToColumnId` è obbligatorio
 * lato backend e indica dove spostarli.
 */
export const useDeleteKanbanColumn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, moveTasksToColumnId }: { id: number; moveTasksToColumnId?: number }) =>
      kanbanColumnsService.deleteColumn(id, moveTasksToColumnId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.all });
      notifications.show({
        title: 'Colonna eliminata',
        message: 'La colonna e stata eliminata con successo',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile eliminare la colonna',
        color: 'red',
      });
    },
  });
};

/**
 * Hook per riordinare le colonne
 */
export const useReorderKanbanColumns = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderKanbanColumnsRequest) => kanbanColumnsService.reorderColumns(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.all });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile riordinare le colonne',
        color: 'red',
      });
    },
  });
};

/**
 * Hook per spostare un task in una colonna. Ottimistico: aggiorna subito ogni board in cache
 * (indipendentemente dai filtri con cui è stata caricata), con rollback in caso di errore.
 * Non invalida più `['tasks']` ad ogni drag: è un refetch inutile e costoso, i dati task
 * "pieni" non cambiano per un semplice spostamento di colonna.
 */
export const useMoveTaskToColumn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: number; data: MoveTaskToDynamicColumnRequest }) =>
      kanbanColumnsService.moveTaskToColumn(taskId, data),
    onMutate: async ({ taskId, data }) => {
      await queryClient.cancelQueries({ queryKey: kanbanColumnKeys.boards() });

      const previousBoards = queryClient.getQueriesData<DynamicKanbanBoard>({
        queryKey: kanbanColumnKeys.boards(),
      });

      previousBoards.forEach(([queryKey, board]) => {
        if (!board) return;
        const updated = moveTaskInBoard(board, taskId, data.columnId, data.beforeTaskId);
        queryClient.setQueryData(queryKey, updated);
      });

      return { previousBoards };
    },
    onError: (error: Error, _variables, context) => {
      context?.previousBoards.forEach(([queryKey, board]) => {
        queryClient.setQueryData(queryKey, board);
      });
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile spostare il task',
        color: 'red',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.boards() });
    },
  });
};

// ============== Alerts Queries & Mutations ==============

/**
 * Hook per ottenere gli alert di scadenza
 */
export const useDeadlineAlerts = () => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: kanbanColumnKeys.alerts(),
    queryFn: kanbanColumnsService.getDeadlineAlerts,
    staleTime: getCacheTime('tasks'),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });
};

/**
 * Hook per posticipare un alert
 */
export const useSnoozeAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SnoozeAlertRequest) => kanbanColumnsService.snoozeAlert(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.alerts() });
      notifications.show({
        title: 'Alert posticipato',
        message: 'L\'alert e stato posticipato',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile posticipare l\'alert',
        color: 'red',
      });
    },
  });
};

/**
 * Hook per dismissare un alert
 */
export const useDismissAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DismissAlertRequest) => kanbanColumnsService.dismissAlert(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.alerts() });
      notifications.show({
        title: 'Alert rimosso',
        message: 'L\'alert e stato rimosso permanentemente',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile rimuovere l\'alert',
        color: 'red',
      });
    },
  });
};

/**
 * Hook per ottenere le preferenze di alert
 */
export const useAlertPreferences = () => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: kanbanColumnKeys.alertPreferences(),
    queryFn: kanbanColumnsService.getAlertPreferences,
    staleTime: getCacheTime('users'),
  });
};

/**
 * Hook per aggiornare le preferenze di alert
 */
export const useUpdateAlertPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserAlertPreferences) => kanbanColumnsService.updateAlertPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.alertPreferences() });
      notifications.show({
        title: 'Preferenze aggiornate',
        message: 'Le preferenze sono state aggiornate',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile aggiornare le preferenze',
        color: 'red',
      });
    },
  });
};
